import * as Reducers from '../../../../../store/reducers';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { NzJustify } from 'ng-zorro-antd/grid';
import { NzMarks } from 'ng-zorro-antd/slider';
import { RangeQuestion } from '@interfaces/questions';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import { isNil } from 'lodash';
interface ITranslationData {
	rangeLow: number;
	rangeHigh: number;
	unit: string;
}

@Component({
	selector: 'workflow-range-question',
	templateUrl: './range-question.component.html',
	styleUrls: ['./range-question.component.scss']
})
export class RangeQuestionComponent implements OnChanges, OnInit, OnDestroy {

	@Input() question: RangeQuestion;
	@Input() justify: NzJustify;
	@Input() isWizard = false;
	@Input() finishedAndLocked = false;
	@Input() isLocked = false;
	@Input() validationLevel: 'warning' | 'error';

	@Output() valid: EventEmitter<boolean> = new EventEmitter();

	validateStatus: string;
	selected: number;
	selected_display: string;
	marks: NzMarks;
	steps = 1;
	language: string;
	markedForChange: boolean;
	errorMessage: string;
	translationData: ITranslationData;

	private selectedInitialized: boolean;
	private timeOut;

	private decimalSign = '.';

	private readonly TIMEOUT_UPDATE = 400;

	private subscription = new Subscription();

	constructor(private workflowItemKey: WorkflowItemKeyDirective, private store: Store<never>, private decimalPipe: DecimalPipe) {
		this.subscription.add(this.store.pipe(select(Reducers.getSettings)).subscribe(state => {
			this.language = state.locale;
			this.decimalSign = this.decimalPipe.transform(1.1, '', this.language).charAt(1);
			this.updateDecimalSign();
		}));
	}

	private updateDecimalSign() {
		this.selected_display = this.RoundBySteps(this.selected)?.toString().replace('.', this.decimalSign);
	}

	ngOnInit(): void {
		this.subscription.add(this.workflowItemKey.property$.subscribe(data => {
			if (!this.finishedAndLocked && !this.selectedInitialized) {
				this.selected = typeof (data) === 'number' ? data as number : undefined;
				this.updateDecimalSign();
				this.selectedInitialized = true;
				this.testValues();
			}
		}));
		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.selected = typeof(data) === 'number' ? data as number : undefined;
				this.updateDecimalSign();
				this.testValues();
			}
		}));
	}

	ngOnDestroy(): void {
		clearTimeout(this.timeOut);
		this.subscription.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.validationLevel) {
			this.testValues();
		}
		if (changes.question) {
			this.translationData = {
				rangeHigh: this.question.rangeHigh,
				rangeLow: this.question.rangeLow,
				unit: this.question.unit ? ' ' + this.question.unit : ''
			};
		}
		if (changes.question && !this.finishedAndLocked) {
			//Selected wird hier gesetzt damit selected und damit die angezeigte Startzahl, nicht null/undefined ist.
			this.marks = new NzMarks();
			const marks = {};
			if (this.question?.rangeLow) {
				this.selected = this.question.rangeLow;
				marks[this.question.rangeLow] = this.question.rangeLow.toString();
			} else {
				this.selected = 0;
				marks[0] = '0';
			}

			if (this.question?.rangeHigh) {
				marks[this.question.rangeHigh] = this.question.rangeHigh.toString();
			} else {
				marks[100] = '100';
			}
			this.marks = marks;

			if (this.question.steps) {
				this.steps = this.question.steps;
			}
			else {
				this.steps = 1;
			}
		}
	}

	changeSelected(faktor: number): void {
		this.selected = Math.max(this.question?.rangeLow || 0,
			Math.min(
				faktor*this.steps + (this.selected || this.question?.rangeLow || 0),
				this.question?.rangeHigh || 100
			)
		);
		this.selected = this.RoundBySteps(this.selected);
		this.updateDecimalSign();
		this.markForChange();
	}

	changeSelectedOnKeypress(event: KeyboardEvent): void {
		const key = event?.key;
		if (this.isLocked || this.finishedAndLocked || (key !== 'ArrowUp' && key !== 'ArrowDown')) return;

		this.changeSelected(key === 'ArrowUp' ? 1 : -1);
	}

	changeImmediately(): void {
		this.markForChange(true);
	}

	exitFocus(): void {
		clearTimeout(this.timeOut);
		if (this.markedForChange) {
			this.markForChange(true);
		}
	}

	changeSelectedDisplay(): void {
		if (!this.selected_display) {
			this.selected = undefined;
			this.validateStatus = undefined;
			this.errorMessage = undefined;
			this.markForChange();
			return;
		}

		const checkParse = Number.parseFloat(this.selected_display.replace(this.decimalSign, '.'));
		if (checkParse.toString().replace('.', this.decimalSign) !== this.selected_display) {
			this.valid.emit(false);
			this.validateStatus = 'error';
			this.errorMessage = 'message.warning.input.invalid';
			return;
		}

		this.selected = Number.parseFloat(this.selected_display.replace(this.decimalSign, '.'));
		if (this.testValues()) {
			this.markForChange();
		}
	}

	private changeValue(): void {
		const hasValidValue = this.testValues();
		if (hasValidValue) {
			this.workflowItemKey.setProperty(this.selected);
		}
		this.resetMarked();
	}

	private resetMarked(): void {
		this.markedForChange = false;
	}

	private RoundBySteps(number: number): number {
		if (!this.steps || isNil(number)) {
			return number;
		}
		if (this.steps.toString().indexOf('.') === -1) {
			return parseFloat(number.toFixed(0));
		}
		const stepsDecimalPlaces = this.steps.toString().split('.')[1].length;

		return parseFloat(number.toFixed(stepsDecimalPlaces));
	}

	private markForChange(immediately: boolean = false): void {
		this.setOutOfRangeFlags();
		this.markedForChange = true;
		clearTimeout(this.timeOut);

		if (immediately) {
			this.changeValue();
		} else {
			this.timeOut = setTimeout(() => {
				this.changeValue();
			}, this.TIMEOUT_UPDATE);
		}
	}

	private setOutOfRangeFlags(): boolean {
		let outOfRangeLow;
		let outOfRangeHigh;
		if (this.question.rangeLow != null) {
			outOfRangeLow = this.selected < this.question.rangeLow;
		} else {
			outOfRangeLow = false;
		}

		if (this.question.rangeHigh != null) {
			outOfRangeHigh = this.selected > this.question.rangeHigh;
		} else {
			outOfRangeHigh = false;
		}

		if (outOfRangeLow) {
			this.errorMessage = 'message.warning.input.rangeLow';
		}
		if (outOfRangeHigh) {
			this.errorMessage = 'message.warning.input.rangeHigh';
		}

		return !(outOfRangeLow || outOfRangeHigh);
	}

	private testValues(): boolean {
		const valid = this.setOutOfRangeFlags();
		this.validateStatus = valid ? 'success' : this.validationLevel;
		this.valid.emit(valid);

		return valid;
	}
}
